import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Link,
  TextField,
  Collapse,
  Paper,
} from '@mui/material';
import {
  Microsoft,
  Security,
  Assessment,
  Business,
  Warning,
  Code,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { AUTH_NOTICE_KEY } from '../api/authFetch';
import kpmgLogo from '../assets/kpmg-logo.png';

/* ── Portal brand colours (Client / SME / EM / Admin) ── */
const PORTAL_COLORS = {
  client: '#00338D',
  sme: '#483698',
  em: '#C6007E',
  admin: '#00B8F1',
};

const Login: React.FC = () => {
  const { login, devLogin, loading, error } = useAuth();

  const [showDevForm, setShowDevForm] = useState(false);
  const [devEmail, setDevEmail] = useState('');
  const [devPassword, setDevPassword] = useState('');
  const [devError, setDevError] = useState<string | null>(null);
  const [devLoading, setDevLoading] = useState(false);
  // One-time notice set by authFetch (e.g. account deactivated).
  const [authNotice, setAuthNotice] = useState<string | null>(() => {
    const notice = sessionStorage.getItem(AUTH_NOTICE_KEY);
    if (notice) sessionStorage.removeItem(AUTH_NOTICE_KEY);
    return notice;
  });

  const clientId = process.env.REACT_APP_AZURE_CLIENT_ID;
  const isConfigured = clientId && clientId !== 'your-client-id-here';
  // Dev password login is opt-in per build; the backend must also enable it.
  const devLoginEnabled = process.env.REACT_APP_ENABLE_DEV_LOGIN === 'true';

  const handleLogin = async () => { await login(); };

  const handleDevLogin = async () => {
    setDevError(null);
    setDevLoading(true);
    try { await devLogin(devEmail, devPassword); }
    catch (err: any) { setDevError(err.message || 'Dev login failed'); }
    finally { setDevLoading(false); }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        /* Gradient mixing Client blue & SME purple */
        background: `linear-gradient(135deg, ${PORTAL_COLORS.client} 0%, ${PORTAL_COLORS.sme} 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* ── Decorative translucent wave layers ── */}
      <Box
        sx={{
          position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none',
          '&::before': {
            content: '""', position: 'absolute', bottom: 0, left: 0, right: 0,
            height: '40%',
            background: 'linear-gradient(0deg, rgba(0,0,0,0.25) 0%, transparent 100%)',
          },
          '&::after': {
            content: '""', position: 'absolute', top: '15%', left: '-10%',
            width: '120%', height: '70%', borderRadius: '50%',
            background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.06) 0%, transparent 70%)',
          },
        }}
      />

      {/* ── KPMG logo top-left ── */}
      <Box sx={{ position: 'absolute', top: 24, left: 32, zIndex: 2 }}>
        <Box component="img" src={kpmgLogo} alt="KPMG" sx={{ height: 40 }} />
      </Box>

      {/* ── Main content ── */}
      <Box
        sx={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          zIndex: 1, px: 2, py: 6,
        }}
      >
        {/* Chevron icon – pointing down toward welcome text */}
        <Box sx={{ mb: 3 }}>
          <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 25 L50 55 L90 25" stroke="rgba(255,255,255,0.45)" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            <path d="M10 50 L50 80 L90 50" stroke="rgba(255,255,255,0.85)" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
        </Box>

        {/* Welcome heading */}
        <Typography
          variant="h3"
          component="h1"
          sx={{
            color: '#fff', fontWeight: 700, textAlign: 'center',
            mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.3)',
            fontSize: { xs: '1.6rem', sm: '2.2rem', md: '2.6rem' },
          }}
        >
          Welcome to KPMG Discovery IQ Platform
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: 'rgba(255,255,255,0.85)', textAlign: 'center', mb: 4,
            fontWeight: 400, fontSize: { xs: '0.95rem', sm: '1.15rem' },
          }}
        >
          Enterprise Multi-Domain Discovery & Assessment portal
        </Typography>

        {/* ── Sign-in card ── */}
        <Paper
          elevation={12}
          sx={{
            width: '100%', maxWidth: 460, borderRadius: 3,
            backdropFilter: 'blur(20px)',
            backgroundColor: 'rgba(255,255,255,0.95)',
            px: { xs: 3, sm: 4 }, py: 4,
          }}
        >
          {/* Configuration Error */}
          {!isConfigured && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <Warning sx={{ verticalAlign: 'middle', mr: 1, fontSize: 16 }} />
                <strong>Microsoft Entra ID Configuration Required</strong>
              </Typography>
              <Typography variant="body2">
                The application is not properly configured for Microsoft Entra ID authentication.
              </Typography>
            </Alert>
          )}

          {/* Error Display */}
          {authNotice && (
            <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAuthNotice(null)}>
              {authNotice}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Sign In button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleLogin}
            disabled={loading || !isConfigured}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Microsoft />}
            sx={{
              py: 1.5, mb: 2, fontWeight: 600, fontSize: '1rem',
              backgroundColor: '#0078d4',
              '&:hover': { backgroundColor: '#106ebe' },
            }}
          >
            {loading ? 'Signing In...' : !isConfigured ? 'Entra ID Not Configured' : 'Sign in'}
          </Button>

          {/* Dev Login toggle */}
          {devLoginEnabled && (<>
          <Divider sx={{ my: 1.5 }}>
            <Typography variant="caption" color="text.secondary">OR</Typography>
          </Divider>

          <Button
            fullWidth variant="outlined" size="small" type="button"
            onClick={(e) => { e.preventDefault(); setShowDevForm(!showDevForm); }}
            startIcon={<Code />}
            sx={{
              mb: 1, borderColor: '#6a1b9a', color: '#6a1b9a',
              '&:hover': { borderColor: '#4a148c', backgroundColor: 'rgba(106,27,154,0.04)' },
            }}
          >
            Sign in as a Dev
          </Button>

          <Collapse in={showDevForm}>
            <Box sx={{ mt: 1, mb: 2 }}>
              {devError && (
                <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDevError(null)}>{devError}</Alert>
              )}
              <TextField fullWidth label="Dev Email" type="email" value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)} sx={{ mb: 1.5 }} size="small" />
              <TextField fullWidth label="Password" type="password" value={devPassword}
                onChange={(e) => setDevPassword(e.target.value)} sx={{ mb: 1.5 }} size="small"
                onKeyDown={(e) => { if (e.key === 'Enter') handleDevLogin(); }} />
              <Button fullWidth variant="contained" type="button"
                onClick={(e) => { e.preventDefault(); handleDevLogin(); }}
                disabled={devLoading || !devEmail || !devPassword}
                startIcon={devLoading ? <CircularProgress size={18} /> : <Code />}
                sx={{ backgroundColor: '#6a1b9a', '&:hover': { backgroundColor: '#4a148c' } }}>
                {devLoading ? 'Signing in…' : 'Sign In'}
              </Button>
            </Box>
          </Collapse>
          </>)}

          {/* Enterprise account notice */}
          <Box sx={{
            mt: 2, p: 1.5, borderRadius: 1,
            backgroundColor: 'rgba(0,51,141,0.06)', border: '1px solid rgba(0,51,141,0.15)',
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              This platform accepts <strong>enterprise Microsoft accounts</strong> from any
              organization. Personal accounts (outlook.com, gmail.com, etc.) are not permitted.
            </Typography>
          </Box>

          {/* Platform Features */}
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="subtitle2" sx={{ color: PORTAL_COLORS.client, fontWeight: 700, mb: 1 }}>
              Platform Features:
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Security sx={{ color: '#2e7d32', mr: 1.5, fontSize: 20 }} />
                <Typography variant="body2">Secure enterprise authentication with Microsoft Entra ID</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Assessment sx={{ color: PORTAL_COLORS.sme, mr: 1.5, fontSize: 20 }} />
                <Typography variant="body2">15 comprehensive assessment domains</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Business sx={{ color: PORTAL_COLORS.admin, mr: 1.5, fontSize: 20 }} />
                <Typography variant="body2">Organization-specific tracking and insights</Typography>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Box>

      {/* ── Footer ── */}
      <Box
        sx={{
          zIndex: 1, pb: 2, px: 3,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5,
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center' }}>
          By signing in, you agree to KPMG Policy on the use of AI tools.
          Learn more about{' '}
          <Link href="https://kpmg.com/xx/en/what-we-do/services/ai/trusted-ai-framework.html"
            target="_blank" rel="noopener noreferrer"
            sx={{ color: '#fff', fontWeight: 600, textDecorationColor: 'rgba(255,255,255,0.5)' }}>
            KPMG Trusted AI Principles
          </Link>.
        </Typography>

        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', mt: 1 }}>
          © KPMG 2026 — Enterprise AI-Powered Assessment Platform
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
